import {Col, Skeleton, Steps, Tag} from 'antd'
import {auth_fetchWorkingProfiles_Api} from 'apis'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useContext, useMemo, useState} from 'react'
import {compose, mapProps, withProps} from 'recompose'
import {withCountries, withProvinces,} from 'views/Account/components/ProfileOptionSelect'
import {NullAsDefault} from 'views/Shared'
import UserContext from '../AppLocals/User/UserContext'
import ProfileQuickView from './components/ProfileQuickView'
import {ItemInformation} from "../AppLocals/Organization/OrganizationEntity";
import {renderIf} from "../Shared";
import getLabelOfGender from "../../helpers/getLabelOfGender";
import {ListHeaderWithLine} from "../AppLocals/Templates/items/ListHeader";
import {formatDate} from "../../helpers/datetimeHelpers";

const DefaultComponent = ({value}) => (
    <p className="text-sm font-medium text-color">{value}</p>
)

const DefaultComponentWrapper = ({children, title}) => {
    const t = useTranslate()
    return (
        <Col
            span={12}
            className="w-full">
            <div className="flex items-center">
                <div className="text-xs uppercase text-color-400 mb-1">{t(title)}</div>
            </div>
            {children}
        </Col>
    )
}

export const profileFields = [
    'privacy',
    'company',
    'end_at',
    'start_at',
    'position',
    'on_working',
    'description',
    'working_address',
    'working_country_id',
    'working_province_id',
]

const formSchema = [
    {
        title: 'position',
        name: 'position',
        component: compose(
            withProps(({value}) => ({
                value,
            }))
        )(DefaultComponent),
    },
    {
        title: 'on working',
        name: 'on_working',
        component: compose(
            withTranslate,
            mapProps(({value, translate}) => ({
                color: value === 1 ? 'processing' : 'default',
                children: translate(!!value ? 'yes' : 'no'),
            }))
        )(Tag),
    },
    {
        title: 'address',
        name: 'working_address',
        component: compose(
            withProps(({value}) => ({
                value,
            }))
        )(DefaultComponent),
    },
    {
        title: 'province',
        name: 'working_province_id',
        component: compose(
            withProvinces,
            mapProps(({value, getProvince = NullAsDefault}) => {
                const [, currentProvince] = getProvince(value)
                return {
                    value: currentProvince,
                }
            })
        )(DefaultComponent),
    },
    {
        title: 'country',
        name: 'working_country_id',
        component: compose(
            withCountries,
            mapProps(({value, getCountry = NullAsDefault}) => {
                const [, currentCountry] = getCountry(value)
                return {
                    value: currentCountry,
                }
            })
        )(DefaultComponent),
    },
    {
        title: 'description',
        name: 'description',
        component: compose(
            withProps(({value}) => ({
                value,
            }))
        )(DefaultComponent),
    },
]


const UserProfile = () => {
    const t = useTranslate()

    const {user} = useContext(UserContext)

    const [currentStep, setStep] = useState(0)

    const apiInfo = auth_fetchWorkingProfiles_Api

    const cacheId = [apiInfo?.path].join('/')

    const {response, isLoading = false} = useAsyncWithCache({
        cacheId,
        apiInfo,
    })

    const profiles = _.get(response, 'data.data', [])

    const onChange = (value) => {
        setStep(value)
    }

    const steps = useMemo(() => {
        return profiles.map((item, index) => {
            return (
                <Steps.Step
                    key={index}
                    description={
                        <ProfileQuickView
                            item={item}
                            compact={true}
                        />
                    }
                />
            )
        })
    }, [profiles])

    const isAdmin = _.get(user, 'edit')
    const isSuperAdmin = _.get(user, 'edit_system')

    const {
        prefix_title,
        name,
        username,
        gender,
        phone_number,
        phone2_number,
        email,
        date_of_birth,
        hometown,
        company,
        school,
        quote,
        total_channels,
        total_followers,
        total_following,
        archdiocese,
        diocese,
        deanery,
        parish,
        patron_saint_name,
    } = user ?? {}

    const ItemStatistic = ({label, value}) => (
        <div className="p-2 md:p-3 flex flex-col justify-between border border-color-50 background shadow-md rounded-xl">
        <span className="font-medium text-xs md:text-sm text-color-500 uppercase leading-snug tracking-wide">
          {t(label)}
        </span>
            <div className="text-right font-bold text-lg text-color-000 leading-snug">
                {value.toLocaleString()}
            </div>
        </div>
    )

    return (
        <Skeleton loading={!!isLoading}>
            <div className="flex flex-col gap-6">
                <div className="grid grid-cols-3 gap-3 md:gap-6">
                    <ItemStatistic label="channel" value={total_channels}/>
                    <ItemStatistic label="followers" value={total_followers}/>
                    <ItemStatistic label="following" value={total_following}/>
                </div>
                <div className="flex flex-col gap-2 mb-3">
                    <ListHeaderWithLine title="personal information" className="text-sm"/>
                    <div className="flex flex-col gap-2">
                        <ItemInformation label="full name" value={`${patron_saint_name || prefix_title || ''} ${name}`}
                                         className="text-sm"/>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {renderIf(
                                date_of_birth,
                                <ItemInformation label="date of birth"
                                                 value={date_of_birth ? formatDate(date_of_birth, 'DD/MM/YYYY') : t('not update')}
                                                 className="text-sm"/>
                            )}
                            {renderIf(
                                gender !== 0,
                                <ItemInformation label="gender" value={t(getLabelOfGender(gender))}
                                                 className="text-sm"/>
                            )}
                        </div>
                        {renderIf(
                            _.some([archdiocese, diocese, deanery, parish]),
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {renderIf(
                                    archdiocese,
                                    <ItemInformation label="archdiocese" value={archdiocese?.name} className="text-sm"/>
                                )}
                                {renderIf(
                                    diocese,
                                    <ItemInformation label="diocese" value={diocese?.name} className="text-sm"/>
                                )}
                                {renderIf(
                                    parish,
                                    <ItemInformation label="parish" value={parish?.name} className="text-sm"/>
                                )}
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {renderIf(
                                isAdmin || isSuperAdmin,
                                <ItemInformation label="access rights"
                                                 value={isSuperAdmin ? 'Super Admin' : isAdmin && 'Admin'}
                                                 className="text-sm"/>
                            )}
                            {renderIf(
                                hometown,
                                <ItemInformation label="hometown" value={hometown} className="text-sm"/>
                            )}
                            {renderIf(
                                company,
                                <ItemInformation label="company" value={company} className="text-sm"/>
                            )}
                            {renderIf(
                                school,
                                <ItemInformation label="school" value={school} className="text-sm"/>
                            )}
                        </div>
                        {renderIf(
                            quote,
                            <div className="flex flex-col gap-1 text-sm italic">
                                <span className="text-color-500">{`${t('quote')} : `}</span>
                                <span className="ml-2 font-bold text-color-100">{quote}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Skeleton>
    )
}

export default UserProfile
